<template>
	<div><I2Label label="Step Details" helpElementName="CommonStepDetails" /></div>
	<div class="mt-5" @mouseover="onStepRowMouseOver" @mouseleave="onStepRowMouseLeave">
		<DataTable
			:value="steps"
			responsiveLayout="scroll"
			:row-hover="true"
			dataKey="id"
			:id="'dtCommonSteps_' + props.generalInformationId"
			@row-reorder="onRowReorder"
			:rowStyle="(data: any) => getRowStyle(data)"
			><!--@cell-edit-complete="onCellEditComplete"-->
			<template #empty> No steps found. </template>
			<template #loading> Loading steps data. Please wait. </template>
			<Column
				:row-reorder="editingSteps.size === 0"
				style="width: 3rem; text-align: center"
				:reorderable-column="false"
				row-reorder-icon="pi pi-arrows-v"
			>
				<template #header><I2Label label="Ordering" helpElementName="CommonStepOrdering" /></template>
			</Column>
			<Column field="id" style="width: 5rem; text-align: center" styleClass="text-center">
				<template #header><I2Label label="Actions" helpElementName="CommonStepActions" /></template>
				<template #body="slotProps"
					><div class="flex flex-row gap-2 space-between">
						<span style="cursor: pointer" v-if="canOpenStepHistory(slotProps.data)" v-tooltip.top="'View change history'"
							><FontAwesomeIcon :icon="['far', 'clock-rotate-left']" class="text-lg" @click="openHistoryDialog(slotProps.data)"
						/></span>
						<span
							:class="editingSteps.size > 0 ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
							v-tooltip.top="editingSteps.size > 0 ? 'Please save or cancel the current step edit before deleting another step.' : lock.tooltip ?? 'Delete'"
							><FontAwesomeIcon
								:icon="['far', 'trash']"
								class="text-lg"
								@click="!assessmentStore.isLocked && deleteStep(slotProps.data)"
								:aria-disabled="editingSteps.size > 0 || assessmentStore.isLocked"
								v-show="
									SessionInfo.hasRightsToScreen('Assessments', PageAccess.Delete) &&
									((!editingSteps.has(slotProps.data.id) && props.tabName === tabsDefinitions.syntheticAPI.value) ||
										(props.tabName !== tabsDefinitions.syntheticAPI.value && !editingSteps.has(slotProps.data.id)))
								"
								v-bind="lock.icon.value"
						/></span>
						<span style="cursor: pointer" v-tooltip.top="lock.tooltip ?? 'Edit'"
							><FontAwesomeIcon
								:icon="['far', 'pen-to-square']"
								class="text-lg"
								@click="!assessmentStore.isLocked && moveToStep(slotProps.data)"
								v-show="
									SessionInfo.hasRightsToScreen('Assessments', PageAccess.Delete) &&
									((!editingSteps.has(slotProps.data.id) && props.tabName === tabsDefinitions.syntheticAPI.value) ||
										(props.tabName !== tabsDefinitions.syntheticAPI.value && !editingSteps.has(slotProps.data.id)))
								"
								v-bind="lock.icon.value"
						/></span></div
				></template>
			</Column>
			<Column field="stepNumber" style="width: 3rem">
				<template #header><I2Label label="Step Number" helpElementName="CommonStepNumber" /></template>
				<template #body="{ data }">
					<div class="w-full text-center" @click="moveToStep(data)">{{ data.stepNumber }}</div>
				</template>
			</Column>
			<Column field="stepName">
				<template #header><I2Label label="Step Name" helpElementName="CommonStepName" /></template>
				<template #body="{ data }">
					<span v-if="props.tabName === tabsDefinitions.syntheticAPI.value"
						><InputText
							v-model="data.stepName"
							class="w-full"
							:id="'stepInput_' + data.id"
							@focus="captureOriginalStepName(data.id)"
							@input="onStepNameInput(data)"
							v-bind="lock.input.value"
							v-tooltip="lock.tooltip"
					/></span>
					<div v-if="props.tabName === tabsDefinitions.biologics.value || props.tabName === tabsDefinitions.productProcess.value">
						<!-- Editable dropdown for Product Process and Biologics: pick a unit operation from the
						list, or type a custom name when the required one is not available. Nothing is persisted
						until the row's Save button (Actions on Step column) is clicked, or Enter is pressed. -->
						<AutoComplete
							:modelValue="data.stepName"
							:suggestions="filteredUnitOperations"
							optionLabel="unitOperationName"
							dropdown
							completeOnFocus
							:placeholder="'Select or type a step name'"
							class="w-full"
							inputClass="w-full"
							:inputId="'stepInput_' + data.id"
							@complete="searchUnitOperations"
							@focus="captureOriginalStepName(data.id)"
							@update:modelValue="onStepNameChanged($event, data)"
							@keyup.enter="saveStep(data)"
							v-bind="lock.select.value"
							v-tooltip="lock.tooltip"
						/>
					</div>
				</template>
				<template #editor="{ data }"> </template>
			</Column>
			<Column v-if="isTemplateCopyEnabled" header="" style="width: 3rem">
				<template #body="{ data }">
					<span v-if="data.templateSourceStepId" style="cursor: pointer" @click="toggleTemplatePopover($event, data)">
						<FontAwesomeIcon :icon="['fas', 'postage-stamp']" class="text-yellow-500" v-tooltip.top="'Copied from template'" />
					</span>
				</template>
			</Column>
			<Column :class="editingSteps.size > 0 ? 'w-40' : 'w-0'">
				<template #header>
					<I2Label v-if="editingSteps.size > 0" label="Actions on Step" helpElementName="CommonStepRowActions" />
				</template>
				<template #body="{ data }">
					<div v-if="editingSteps.has(data.id)" class="flex gap-2">
						<Button label="Save" class="p-button-sm p-button-success" @click="saveStep(data)" v-bind="lock.button.value" v-tooltip="lock.tooltip" />
						<Button label="Cancel" class="p-button-sm p-button-danger" @click="cancelStep(data)" />
					</div>
				</template>
			</Column>
		</DataTable>
		<I2FormRow class="mt-3">
			<I2FormField style="width: 3%">&nbsp;</I2FormField>
			<I2FormField style="width: 47%">
				<!-- <Button id="btnAddStep" class="btn p-button inverted" label="Add Step" icon="pi pi-plus-circle" @click="addStep()"></Button> -->
				<Button
					id="btnAddStep"
					class="btn btn-outline"
					label="Add Step"
					icon="pi pi-plus-circle"
					@click="addStep()"
					:disabled="editingSteps.size > 0 || lock.isLocked.value"
					v-tooltip="lock.tooltip"
				></Button>
			</I2FormField>
			<I2FormField style="width: 47%">
				<!-- <Button id="btnAddStep" class="btn p-button inverted" label="Add Step" icon="pi pi-plus-circle" @click="addStep()"></Button> -->
				<Button
					id="btnCopyStep"
					class="btn btn-outline"
					label="Copy Step"
					icon="pi pi-copy"
					@click="openModalCopyStep()"
					:disabled="editingSteps.size > 0 || lock.isLocked.value"
					v-tooltip="lock.tooltip"
				></Button>
			</I2FormField>
			<I2FormField style="width: 50%">&nbsp;</I2FormField>
		</I2FormRow>
	</div>
	<Dialog v-model:visible="showDialogDelete" :modal="true" style="width: 35vw" :closable="false" contentStyle="overflow:visible">
		<template #header>
			<PageLabel :helpPageName="'projectDelete'">Delete Step </PageLabel>
		</template>
		<FormCard>
			<I2FormRow class="mt-6">
				<h1>Are you sure you would like to delete selected Step?</h1>
			</I2FormRow>
		</FormCard>
		<template #footer>
			<I2FormRow right>
				<Button label="Cancel" class="inverted mr-4" @click="showDialogDelete = false" right :disabled="deleteStepIsRunning" />
				<Button class="ml-4" @click="deleteStepOK" :disabled="deleteStepIsRunning" v-bind="lock.input.value" v-tooltip="lock.tooltip">
					<template #default>
						<span v-if="deleteStepIsRunning">
							<FontAwesomeIcon :icon="['fa', 'spinner']" spin class="mr-2" />
							Deleting...
						</span>
						<span v-else> Delete </span>
					</template>
				</Button>
			</I2FormRow>
		</template>
	</Dialog>
	<div v-if="showCopyStepDialog">
		<copyStepDialog
			:showDialog="true"
			:screenId="props.screenId"
			:assessmentId="props.assessmentId"
			:tabName="props.tabName"
			:actualSteps="steps"
			:spinnerRuns="copyStepSpinner"
			:isTemplateCopyEnabled="props.isTemplateCopyEnabled"
			@cancel="hideCopyStepDialog()"
			@save="saveCopiedStep"
		/>
	</div>
	<Popover v-if="isTemplateCopyEnabled" ref="templatePopoverRef">
		<div class="p-3" style="min-width: 280px">
			<div class="text-sm font-semibold mb-2">Copied from Template</div>
			<div class="text-xs text-gray-600 mb-1"><span class="font-medium">Project:</span> {{ templatePopoverData.projectName }}</div>
			<div class="text-xs text-gray-600 mb-1"><span class="font-medium">Subproject:</span> {{ templatePopoverData.subProjectName }}</div>
			<div class="text-xs text-gray-600 mb-1"><span class="font-medium">Assessment:</span> {{ templatePopoverData.assessmentName }}</div>
			<div v-if="templatePopoverData.screenName" class="text-xs text-gray-600 mb-1">
				<span class="font-medium">Screen:</span> {{ templatePopoverData.screenName }}
			</div>
			<div class="text-xs text-gray-600 mb-3"><span class="font-medium">Original Step:</span> {{ templatePopoverData.stepName }}</div>
			<div v-if="templatePopoverData.graph && templatePopoverData.graph.steps.length > 0">
				<div class="text-xs font-medium mb-1">Original Step Sequence:</div>
				<VueFlowMiniPreview
					:steps="templatePopoverData.graph.steps"
					:connections="templatePopoverData.graph.connections"
					:highlightIndex="templatePopoverData.highlightIndex"
				/>
			</div>
		</div>
	</Popover>
</template>
<script setup lang="ts">
import { ref, computed, onMounted, watchEffect, watch, PropType, nextTick } from "vue"
import AutoComplete, { AutoCompleteCompleteEvent } from "primevue/autocomplete"
import InputText from "primevue/inputtext"
import Button from "primevue/button"
import {
	ApiClient,
	Assessment,
	Screen,
	GeneralInformation,
	Step,
	SiteLocation,
	ConsumerEolMaterialDefault,
	UnitOperation,
} from "../../entities/NSwagGenerated"
import { SessionInfo, PageAccess } from "../auth/personSessionData"
import { useToast } from "primevue/usetoast"
import { EventBus } from "@/eventBus"
import DataTable from "primevue/datatable"
import Column from "primevue/column"
import Dialog from "primevue/dialog"

import copyStepDialog from "./copyStepDialog.vue"

import { library } from "@fortawesome/fontawesome-svg-core"
import {
	faInfoCircle,
	faTrash,
	faPen,
	faChartLine,
	faCopy,
	faChevronDoubleRight,
	faPenToSquare,
	faPostageStamp,
	faClockRotateLeft,
} from "@fortawesome/pro-regular-svg-icons"
import { faSpinner } from "@fortawesome/free-solid-svg-icons"
import { faPostageStamp as faPostageStampSolid } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome"
import { useTabsDefinitions } from "../composables/tabsDefinitions"
import { buildTemplateGroupColorMap } from "../composables/stepStyles"
import CopyStepDialog from "./copyStepDialog.vue"
import Popover from "primevue/popover"
import VueFlowMiniPreview from "./VueFlowMiniPreview.vue"
import { useAssessmentStore } from "@/store/assessmentStore"
import { useAssessmentLock } from "@/composables/assessmentLock"
import { useTemporalHistoryStore } from "@/store/temporalHistoryStore"

const assessmentStore = useAssessmentStore()
const lock = useAssessmentLock(assessmentStore)
const tabsDefinitions = useTabsDefinitions()
const historyStore = useTemporalHistoryStore()
library.add(
	faTrash,
	faPen,
	faInfoCircle,
	faChartLine,
	faCopy,
	faChevronDoubleRight,
	faSpinner,
	faPenToSquare,
	faPostageStamp,
	faPostageStampSolid,
	faClockRotateLeft
)
const apiClient = new ApiClient(import.meta.env.VITE_API_URL?.toString())
const props = defineProps({
	assessmentId: {
		type: String,
		required: true,
		default: "",
	},
	screenId: {
		type: String,
		required: true,
		default: "",
	},
	generalInformationId: {
		type: String,
		required: true,
		default: "",
	},
	tabName: {
		type: String,
		required: true,
		default: "",
	},
	selectedCompOptions: {
		type: Array,
		required: false,
		default: function () {
			return []
		},
	},
	allTabsInformation: {
		type: Object,
		required: true,
		default: "",
	},
	highlightedStepId: {
		type: String as PropType<string | null>,
		required: false,
		default: null,
	},
	isTemplateCopyEnabled: {
		type: Boolean,
		required: false,
		default: false,
	},
})
//const screenId = "A001236E-1BB5-43E7-93BD-C077B4B473DE"
let generalInformationId = ref()
generalInformationId.value = props.generalInformationId
let steps = ref([] as Step[])
let siteLocationList = ref([] as SiteLocation[])
let prevStepNames = [] as string[]
let originalStepNames = new Map<string, string>() // Map to store original step names for cancel functionality
let editingSteps = ref(new Set<string>()) // Set to track which steps are being edited
let toast = useToast()

// Template popover state
const templatePopoverRef = ref()
const templatePopoverData = ref<{
	projectName: string
	subProjectName: string
	assessmentName: string
	screenName: string
	stepName: string
	graph: { steps: { label: string }[]; connections: { from: number; to: number }[] } | null
	highlightIndex: number
}>({
	projectName: "",
	subProjectName: "",
	assessmentName: "",
	screenName: "",
	stepName: "",
	graph: null,
	highlightIndex: -1,
})

const templateGroupColorMap = computed(() => buildTemplateGroupColorMap(steps.value))
const hoveredStepId = ref<string | null>(null)

// Watch prop from graph hover → highlight step row
watch(
	() => props.highlightedStepId,
	(newId) => {
		hoveredStepId.value = newId
	}
)

function getRowStyle(data: Step) {
	const style: Record<string, string> = {}
	if (props.isTemplateCopyEnabled) {
		const groupId = data.templateGroupId
		if (groupId) {
			const color = templateGroupColorMap.value.get(groupId)
			if (color) style.backgroundColor = color
		}
	}
	if (hoveredStepId.value === data.id) {
		style.filter = "brightness(0.92)"
		style.boxShadow = "inset 0 0 0 2px #6B7280"
	}
	return style
}

function onStepRowMouseOver(event: MouseEvent) {
	const row = (event.target as HTMLElement).closest("tr")
	if (!row) return
	const tbody = row.closest("tbody")
	if (!tbody) return
	const rowIndex = Array.from(tbody.children).indexOf(row)
	if (rowIndex < 0 || rowIndex >= steps.value.length) return
	const stepId = steps.value[rowIndex].id ?? null
	if (stepId !== hoveredStepId.value) {
		hoveredStepId.value = stepId
		emit("stepHovered", stepId)
	}
}

function onStepRowMouseLeave() {
	hoveredStepId.value = null
	emit("stepHovered", null)
}

function toggleTemplatePopover(event: Event, data: Step) {
	templatePopoverData.value.projectName = data.templateSourceProjectName ?? ""
	templatePopoverData.value.subProjectName = data.templateSourceSubProjectName ?? ""
	templatePopoverData.value.assessmentName = data.templateSourceAssessmentName ?? ""
	templatePopoverData.value.stepName = data.templateSourceStepName ?? ""
	templatePopoverData.value.screenName = ""
	templatePopoverData.value.graph = null
	templatePopoverData.value.highlightIndex = -1

	if (data.templateSourceGraphJson) {
		try {
			const graphData = JSON.parse(data.templateSourceGraphJson)
			templatePopoverData.value.screenName = graphData.steps?.[0]?.screenName ?? ""
			const originalStepName = data.templateSourceStepName ?? ""
			templatePopoverData.value.highlightIndex = graphData.steps.findIndex((s: any) => s.originalStepName === originalStepName)
			templatePopoverData.value.graph = {
				steps: graphData.steps.map((s: any) => ({
					label: s.originalStepName ? `${s.originalStepName} (${s.label})` : s.label,
				})),
				connections: graphData.connections ?? [],
			}
		} catch {
			// ignore parse errors
		}
	}

	templatePopoverRef.value.toggle(event)
}

//editing
const editMode = ref()
editMode.value = "cell"

//delete step
const showDialogDelete = ref(false)
const selectedStepToDelete = ref<Step | null>(null)

const unitOperations = ref()

const copyStepSpinner = ref(false)

const deleteStepIsRunning = ref(false)

watch(
	() => props.selectedCompOptions,
	(newVal, oldVal) => {
		unitOperations.value = newVal
	}
)

onMounted(() => {
	getAllSiteLocation()
	if (props.tabName === tabsDefinitions.productProcess.value) {
		getStandardUnitOperations()
	} else {
		getUnitOperations()
	}

	EventBus.on("newGeneralInformation", () => {
		steps.value = []
	})

	EventBus.on("saveGeneralInformation", () => {
		saveAllSteps()
	})

	EventBus.on("versionChange", (data) => {
		generalInformationId.value = data
		steps.value = []
		prevStepNames = [] as string[]
		getSteps()
	})

	EventBus.on("addStep_" + props.generalInformationId, () => {
		window.scrollTo(0, document.body.scrollHeight)
		addStep()
	})
})

//copy step

const showCopyStepDialog = ref(false)

const openModalCopyStep = () => {
	showCopyStepDialog.value = true
}
const saveCopiedStep = (stepId: string, stepName: string, copyUpstream: boolean, upstreamNames?: Record<string, string>) => {
	copyStepSpinner.value = true
	if (copyUpstream) {
		const upstreamNamesJson = upstreamNames ? JSON.stringify(upstreamNames) : undefined
		apiClient
			.copyStepsWithUpstream(stepId, props.assessmentId, props.generalInformationId, stepName, true, upstreamNamesJson)
			.then(
				(res: Step[]) => {
					// res is an array of steps - add all of them
					if (res) res.forEach((s: Step) => steps.value.push(s))
					EventBus.emit("loadStepListBox_" + props.screenId, { screenId: props.screenId, generalInformationId: props.generalInformationId })
				},
				(err: any) => {
					if (err.status == 409) {
						toast.add({ severity: "warn", summary: `Error: ${err.response.message}`, life: 3000 })
					} else {
						toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
					}
				}
			)
			.finally(() => {
				copyStepSpinner.value = false
				showCopyStepDialog.value = false
			})
	} else {
		apiClient.copyStepToAssessment(stepId, props.assessmentId, props.generalInformationId, stepName).then(
			(res) => {
				steps.value.push(res)
				EventBus.emit("loadStepListBox_" + props.screenId, { screenId: props.screenId, generalInformationId: props.generalInformationId })
				showCopyStepDialog.value = false
				copyStepSpinner.value = false
			},
			(err) => {
				if (err.status == 409) {
					toast.add({ severity: "warn", summary: `Error: ${err.response.message}`, life: 3000 })
				} else {
					toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
				}
				copyStepSpinner.value = false
			}
		)
	}
}
const hideCopyStepDialog = () => {
	showCopyStepDialog.value = false
}

function moveToStep(data: any) {
	EventBus.emit("selectAStep_" + props.screenId, data)
}

async function deleteStepOK() {
	const stepToDelete = selectedStepToDelete.value
	if (!stepToDelete || deleteStepIsRunning.value) return

	deleteStepIsRunning.value = true
	let deleteCompleted = false

	try {
		stepToDelete.isActive = false
		await apiClient.updateStepData("Delete", stepToDelete)
		deleteCompleted = true

		const index = steps.value.findIndex((item) => item.id === stepToDelete.id)
		if (index !== -1) {
			steps.value.splice(index, 1)
			prevStepNames.splice(index, 1)
		}

		const persistedSteps = steps.value.filter((step) => step.id && !editingSteps.value.has(step.id))
		steps.value = (await apiClient.updateStepsOrder(persistedSteps)) ?? persistedSteps

		EventBus.emit("loadStepListBox_" + props.screenId, { screenId: props.screenId, generalInformationId: props.generalInformationId })
		if (steps.value.length === 0) {
			EventBus.emit("selectAStep_" + props.screenId, new Step())
		} else {
			EventBus.emit("selectAStep_" + props.screenId, steps.value[0])
		}

		showDialogDelete.value = false
		selectedStepToDelete.value = null
		toast.add({ severity: "success", summary: "Step deleted", detail: "The step was deleted successfully.", life: 3000 })
	} catch {
		if (deleteCompleted) {
			showDialogDelete.value = false
			selectedStepToDelete.value = null
			EventBus.emit("loadStepListBox_" + props.screenId, { screenId: props.screenId, generalInformationId: props.generalInformationId })
			toast.add({
				severity: "warn",
				summary: "Step deleted",
				detail: "The step was deleted, but the remaining step order could not be refreshed. Please reload the page.",
				life: 5000,
			})
		} else {
			stepToDelete.isActive = true
			toast.add({ severity: "error", summary: "Delete failed", detail: "The step could not be deleted. Please try again.", life: 5000 })
		}
	} finally {
		deleteStepIsRunning.value = false
	}
}

function deleteStep(ev: Step) {
	if (editingSteps.value.size > 0) {
		toast.add({
			severity: "warn",
			summary: "Unsaved step",
			detail: "Please save or cancel the current step edit before deleting another step.",
			life: 5000,
		})
		return
	}

	selectedStepToDelete.value = ev
	if (ev.stepName !== undefined) showDialogDelete.value = true
	if (ev.stepName === undefined) {
		const index = steps.value.findIndex((item) => item.id === ev.id)
		steps.value.splice(index, 1)
		prevStepNames.splice(index, 1)
	}
}

// function onCellEditComplete(ev: any) {
// 	if (props.tabName === "Synthetic API") {
// 		if (ev.newData.stepName === undefined) return
// 		const index = steps.value.findIndex((item) => item.id === ev.newData.id)
// 		if (index !== -1) {
// 			Object.assign(steps.value[index], ev.newData)
// 			apiClient.updateStepData(ev.newData).then((res) => {
// 				if (ev.newData !== ev.data)
// 			})
// 		}
// 	} else {
// 		if (ev.newData.unitOperationId === undefined) return
// 		const index = steps.value.findIndex((item) => item.id === ev.newData.id)
// 		if (index !== -1) {
// 			ev.newData.stepName = unitOperations.value.filter((item) => item.id === ev.newData.unitOperationId)[0].unitOperationName
// 			Object.assign(steps.value[index], ev.newData)
// 			apiClient.updateStepData(ev.newData).then((res) => {
// 				if (ev.newData !== ev.data)
// 			})
// 		}
// 	}
// 	let stepIndex = steps.value.length - 1
// 	steps.value[stepIndex].isEditing = false
// }
const setTabInfoGlobally = (type: string, tabId: string, tabContent: any) => {
	// 	"setTabInfoGlobally",
	// 	type,
	// 	tabId,
	// 	tabContent,
	// 	props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId)
	// )

	switch (type) {
		case "selectedStepId":
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId).selectedStepId = tabContent
			break
		case "generalInformationId":
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId).generalInformationId = tabContent
			break
		case "actualSubTabName":
			props.allTabsInformation.allTabsStatus.find((item: any) => item.actualMainTabId === tabId).actualSubTabName = tabContent
			break
	}
	emit("updateAllTabsInformation", props.allTabsInformation)
}
// Store the original name when the user focuses on the input (before any changes), so Cancel can
// restore it. Only store if the step has a name (existing step), not for new empty steps.
function captureOriginalStepName(id: string) {
	const stepObj = steps.value.find((a) => a.id === id)
	if (!originalStepNames.has(id) && stepObj?.stepName && stepObj.stepName.trim() !== "") {
		originalStepNames.set(id, stepObj.stepName)
	}
}

// Suggestions currently shown by the step name AutoComplete.
const filteredUnitOperations = ref([] as UnitOperation[])

function searchUnitOperations(event: AutoCompleteCompleteEvent) {
	const query = event.query.trim().toLowerCase()
	const allUnitOperations = (unitOperations.value ?? []) as UnitOperation[]
	filteredUnitOperations.value =
		query === "" ? [...allUnitOperations] : allUnitOperations.filter((item) => (item.unitOperationName ?? "").toLowerCase().includes(query))
}

// update:modelValue fires for both typing (string payload) and picking a suggestion (the full
// UnitOperation object -- AutoComplete has no optionValue configured, so getOptionValue just
// returns the option as-is, same object option-select would have given us). Either way, this is
// a genuine change made by the user, so the row is marked as editing to reveal Save/Cancel.
function onStepNameChanged(value: string | UnitOperation, step: Step) {
	if (typeof value === "string") {
		step.stepName = value
	} else {
		step.unitOperationId = value.id
		step.stepName = value.unitOperationName ?? ""
	}
	if (step.id) markStepEditing(step.id)
}
const emit = defineEmits(["updateAllTabsInformation", "stepHovered"])

function onRowReorder(event: any) {
	apiClient.updateStepsOrder(event.value).then((res) => {
		steps.value = res
		EventBus.emit("loadStepListBox_" + props.screenId, { screenId: props.screenId, generalInformationId: props.generalInformationId })
	})
}

function getAllSiteLocation() {
	apiClient.siteLocationsAll().then(
		(res: any) => {
			siteLocationList.value = res
			//get steps by assessmentId, screenId, generalInformationId
			getSteps()
		},
		(err) => {
			toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
		}
	)
}

function getUnitOperations() {
	apiClient.getUnitOperationByScreenAssessment(props.assessmentId, props.screenId).then((res) => {
		unitOperations.value = res
	})
}

function getStandardUnitOperations() {
	// Get only unit operations that belong to the "Standard" compOption
	apiClient.getUnitOperationsByCompOptionName("Standard", props.screenId).then(
		(res: any) => {
			unitOperations.value = res
		},
		(err: any) => {
			console.error("Error loading Standard unit operations:", err)
			toast.add({ severity: "error", summary: "Error loading dose forms", detail: "Could not load Standard dose forms", life: 3000 })
		}
	)
}

function getSteps() {
	// if (props.tabName === "Synthetic API") {
	apiClient.getStepByAssessmentIdScreenIdGeneralInfomationId(props.assessmentId, props.screenId, generalInformationId.value).then(
		(res) => {
			if (res && res != null && res.length > 0) {
				steps.value = res
				let resLength = res.length
				if (prevStepNames.length === 0) {
					for (let x = 0; x < resLength; x++) {
						prevStepNames[x] = res[x].stepName ?? ""
					}
				}
			}
		},
		(err) => {
			toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
		}
	)
	// }
}

function addStep() {
	EventBus.emit("addStepClicked")
	let step = new Step()
	step.id = self.crypto.randomUUID()
	step.assessmentId = props.assessmentId
	step.screenId = props.screenId
	step.generalInformationId = props.generalInformationId
	//get last step number
	let lastStepNumber = getLastStepNumber()
	step.stepNumber = lastStepNumber + 1
	step.isActive = true
	steps.value.push(step)

	// Mark the new step as editing
	editingSteps.value.add(step.id!)

	// Focus on the new step input after it's rendered
	nextTick(() => {
		const input = document.getElementById("stepInput_" + step.id) as HTMLInputElement
		if (input) {
			input.focus()
		}
	})
}

function canOpenStepHistory(step: Step) {
	return Boolean(step?.id && step?.stepName?.trim() && !editingSteps.value.has(step.id))
}

function openHistoryDialog(step: Step) {
	if (!canOpenStepHistory(step)) return

	const historyTitle = step.stepName?.trim() ? `${step.stepNumber ?? ""} - ${step.stepName}` : `Step ${step.stepNumber ?? ""}`

	historyStore.loadHistory(
		{
			entity: "step",
			projections: ["createdByPersonId", "updatedByPersonId", "screenId", "siteLocationId", "unitOperationId"],
			relations: [
				{
					entity: "utilities",
					fk: "stepId",
					projections: ["createdByPersonId", "updatedByPersonId", "facilityTypeId", "cleanRoomId", "WFIId", "coldStorageId", "locationId", "unitId"],
				},
				{
					entity: "utilitiesSteamConsumption",
					fk: "stepId",
					projections: ["createdByPersonId", "updatedByPersonId"],
				},
				{
					entity: "utilitiesEnergyConsumption",
					fk: "stepId",
					projections: ["createdByPersonId", "updatedByPersonId"],
				},
				{
					entity: "rawMaterial",
					fk: "stepId",
					projections: ["createdByPersonId", "updatedByPersonId", "siteLocationId", "unitId"],
					relations: [
						{
							entity: "rawMaterialUnitOperation",
							fk: "rawMaterialId",
							projections: ["createdByPersonId", "updatedByPersonId", "unitOperationId", "unitId"],
						},
					],
				},
				{
					entity: "rawMaterialEntry",
					fk: "stepId",
					projections: [
						"createdByPersonId",
						"updatedByPersonId",
						"materialId",
						"unitId",
						"unitOperationId",
						"dataQualityRatingId",
						"packagingTypeId",
					],
				},
				{
					entity: "materialInboundTransport",
					fk: "stepId",
					projections: [
						"createdByPersonId",
						"updatedByPersonId",
						"transportId",
						"unitId",
						"materialId",
						"intermediateStepId",
						"productProcessProxyId",
					],
				},
				{
					entity: "intermediateStep",
					fk: "stepId",
					projections: ["createdByPersonId", "updatedByPersonId", "intermediateStepId", "unitId", "unitOperationId"],
				},
				{
					entity: "productProcessProxyEntry",
					fk: "stepId",
					projections: ["createdByPersonId", "updatedByPersonId", "productProcessProxyId", "unitId", "unitOperationId"],
				},
				{
					entity: "processWaste",
					fk: "stepId",
					projections: ["createdByPersonId", "updatedByPersonId", "pmUnitId", "pmOutputUnitId", "eolDestinationMarket", "eolDestinationSetting"],
				},
				{
					entity: "processWasteDetail",
					fk: "stepId",
					projections: ["createdByPersonId", "updatedByPersonId", "materialId", "wasteTypeId", "rawMaterialEntryId"],
				},
			],
		},
		{
			step: {
				label: "Step",
				fields: [
					"stepName",
					"stepNumber",
					"templateSourceStepName",
					"templateSourceProjectName",
					"templateSourceSubProjectName",
					"templateSourceAssessmentName",
				],
				projections: {
					screenId: { label: "Module", nameField: "screenName", alwaysShow: false },
					siteLocationId: { label: "Site Location", nameField: "siteLocationName", alwaysShow: false },
					unitOperationId: { label: "Unit Operation", nameField: "unitOperationName", alwaysShow: false },
				},
			},
			utilities: {
				label: "Utilities",
				fields: ["notes", "hvacRequired", "roomArea", "totalStepDuration", "isColdStorage", "storageTime"],
				projections: {
					facilityTypeId: { label: "Facility Type", nameField: "isoClassFacilityTypeName", alwaysShow: false },
					cleanRoomId: { label: "Clean Room Class", nameField: "hvacClassName", alwaysShow: false },
					WFIId: { label: "WFI Source", nameField: "wfiSourceName", alwaysShow: false },
					coldStorageId: { label: "Temperature Requirement", nameField: "temperatureRequirement", alwaysShow: false },
					locationId: { label: "Cold Storage Location", nameField: "siteLocationName", alwaysShow: false },
					unitId: { label: "Cold Storage Unit", nameField: "unitName", alwaysShow: false },
				},
			},
			utilitiesSteamConsumption: {
				label: "Utilities Steam Consumption",
				fields: ["naturalGas", "coal", "diesel", "biomass"],
			},
			utilitiesEnergyConsumption: {
				label: "Utilities Energy Consumption",
				fields: ["gridAverageMix", "gridGreenTariff", "solar", "wind", "onSiteHydro", "onSiteBiomass", "renewable"],
			},
			rawMaterialEntry: {
				label: "Raw Material",
				fields: ["componentName", "quantity", "notes", "supplierName", "supplierLocation", "referenceQuantity"],
				projections: {
					materialId: { label: "Material", nameField: "materialName", alwaysShow: true },
					unitId: { label: "Unit", nameField: "unitName", alwaysShow: false },
					unitOperationId: { label: "Unit Operation", nameField: "unitOperationName", alwaysShow: false },
					dataQualityRatingId: { label: "Data Quality Rating", nameField: "dataQualityRatingId", alwaysShow: false },
					packagingTypeId: { label: "Packaging Type", nameField: "valueTxt", alwaysShow: false },
				},
			},
			rawMaterial: {
				label: "Step Output",
				fields: ["massOfProduct"],
				projections: {
					siteLocationId: { label: "Site Location", nameField: "siteLocationName", alwaysShow: false },
					unitId: { label: "Unit", nameField: "unitName", alwaysShow: false },
				},
			},
			rawMaterialUnitOperation: {
				label: "Step Output Unit Operation",
				fields: ["duration"],
				projections: {
					unitOperationId: { label: "Unit Operation", nameField: "unitOperationName", alwaysShow: true },
					unitId: { label: "Duration Unit", nameField: "unitName", alwaysShow: false },
				},
			},
			materialInboundTransport: {
				label: "Inbound Transport",
				fields: ["distance"],
				projections: {
					transportId: { label: "Transport", nameField: "transportName", alwaysShow: true },
					unitId: { label: "Distance Unit", nameField: "unitName", alwaysShow: false },
					materialId: { label: "Material", nameField: "materialName", alwaysShow: true },
					intermediateStepId: { label: "Intermediate Step", nameField: "stepName", alwaysShow: true },
					productProcessProxyId: { label: "Proxy Material", nameField: "materialName", alwaysShow: true },
				},
			},
			intermediateStep: {
				label: "Intermediate Step Material",
				fields: ["quantity", "notes"],
				projections: {
					intermediateStepId: { label: "Intermediate Step", nameField: "stepName", alwaysShow: true },
					unitId: { label: "Unit", nameField: "unitName", alwaysShow: false },
					unitOperationId: { label: "Unit Operation", nameField: "unitOperationName", alwaysShow: false },
				},
			},
			productProcessProxyEntry: {
				label: "Proxy Material",
				fields: ["quantity", "notes", "supplierName", "supplierLocation"],
				projections: {
					productProcessProxyId: { label: "Proxy Material", nameField: "materialName", alwaysShow: true },
					unitId: { label: "Unit", nameField: "unitName", alwaysShow: false },
					unitOperationId: { label: "Unit Operation", nameField: "unitOperationName", alwaysShow: false },
				},
			},
			processWaste: {
				label: "Process Waste",
				fields: ["notes", "pmConcentration", "pmOutput"],
				projections: {
					pmUnitId: { label: "PM Concentration Unit", nameField: "unitName", alwaysShow: false },
					pmOutputUnitId: { label: "PM Output Unit", nameField: "unitName", alwaysShow: false },
					eolDestinationMarket: { label: "Destination Market", nameField: "valueTxt", alwaysShow: false },
					eolDestinationSetting: { label: "Destination Setting", nameField: "valueTxt", alwaysShow: false },
				},
			},
			processWasteDetail: {
				label: "Process Waste Detail",
				fields: [
					"processWasteDetailName",
					"totalMass",
					"sterilizationBeforeDisposal",
					"hazardWasteIncineration",
					"landfill",
					"recycling",
					"wasteIncinerationEnergyRecovery",
					"wasteIncinerationNoEnergyRecovery",
					"municipalWaste",
				],
				projections: {
					rawMaterialEntryId: { label: "Component", nameField: "componentName", alwaysShow: true },
					materialId: { label: "Material", nameField: "materialName", alwaysShow: true },
					wasteTypeId: { label: "Waste Type", nameField: "wasteTypeName", alwaysShow: true },
				},
			},
		},
		{ id: step.id },
		`Change History: ${historyTitle}`.trim()
	)
}

function getLastStepNumber() {
	let lastStepNumber = 0

	if (steps && steps.value != null && steps.value.length > 0) {
		lastStepNumber = steps.value.length
	}

	return lastStepNumber
}

// Marks a step as being edited so the row's Save/Cancel buttons appear, and restores focus to
// its input once the Actions column has rendered (which shifts layout).
function markStepEditing(id: string) {
	if (editingSteps.value.has(id)) return
	editingSteps.value.add(id)
	nextTick(() => {
		const input = document.getElementById("stepInput_" + id) as HTMLInputElement
		if (input) {
			input.focus()
		}
	})
}

// Handle input changes to mark step as editing
function onStepNameInput(step: Step) {
	markStepEditing(step.id!)
}

// Save a single step (new or edited)
function saveStep(step: Step) {
	if (!step.stepName || step.stepName.trim() === "") {
		toast.add({ severity: "warn", summary: "Step name is required", life: 2000 })
		return
	}

	// Biologics / Product Process: a name typed by hand (rather than picked from the dropdown) may
	// still exactly match a real unit operation, so link it the same way selecting it would have.
	if (props.tabName === tabsDefinitions.biologics.value || props.tabName === tabsDefinitions.productProcess.value) {
		const typedName = step.stepName.trim()
		const matchedUnitOperation = ((unitOperations.value ?? []) as UnitOperation[]).find(
			(item) => (item.unitOperationName ?? "").trim().toLowerCase() === typedName.toLowerCase()
		)
		step.unitOperationId = matchedUnitOperation?.id
		step.stepName = matchedUnitOperation?.unitOperationName ?? typedName
	}

	const index = steps.value.findIndex((item) => item.id === step.id)
	if (index !== -1) {
		prevStepNames[index] = step.stepName
	}

	apiClient
		.updateStepData("StepNameChange", step)
		.then(() => {
			editingSteps.value.delete(step.id!)
			originalStepNames.delete(step.id!)
			EventBus.emit("loadStepListBox_" + props.screenId, { screenId: props.screenId, generalInformationId: props.generalInformationId })
			EventBus.emit("selectedStep_" + props.screenId, step)
			EventBus.emit("highlightStep_" + props.screenId, step)
			EventBus.emit("checkStepIdSelected_" + props.screenId)
			setTabInfoGlobally("selectedStepId", props.screenId, step.id)
		})
		.catch((err) => {
			toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
		})
}

// Cancel step edit or remove new step
function cancelStep(step: Step) {
	const index = steps.value.findIndex((item) => item.id === step.id)

	if (index !== -1) {
		// If step has no original name, it's a new step - remove it
		if (!originalStepNames.has(step.id!)) {
			steps.value.splice(index, 1)
			prevStepNames.splice(index, 1)
			editingSteps.value.delete(step.id!)
		} else {
			// Restore original name
			step.stepName = originalStepNames.get(step.id!)
			editingSteps.value.delete(step.id!)
			originalStepNames.delete(step.id!)
		}
	}
}

// Save all steps (called from EventBus)
function saveAllSteps() {
	apiClient.postSaveStep(props.assessmentId, props.screenId, props.generalInformationId, steps.value).then(
		() => {},
		(err) => {
			toast.add({ severity: "error", summary: `Error: ${err}`, life: 2000 })
		}
	)
}
</script>
<style scoped>
.btn-outline {
	background-color: white;
	color: #007bff;
	border: 2px solid #007bff;
	padding: 5px 10px;
	font-size: 12px;
	border-radius: 3px;
	cursor: pointer;
	transition: all 0.3s ease;
}

.btn-custom:hover {
	background-color: #007bff;
	color: white;
}
</style>
